import {
  ACTIVATE_ACCOUNT,
  DELETE_ADDRESS,
  DELETE_SAVED_VEHICLE,
  REGISTER_GUEST_CHECKOUT_USER,
  RESET_PASSWORD,
  SEND_AUTHENTICATION_EMAIL,
  SET_ACTIVE_STATUS_SAVED_VEHICLE,
  SIGN_IN,
  SIGN_OUT,
  SIGN_UP,
  UPDATE_EMAIL,
  UPDATE_PASSWORD,
  UPDATE_PRIMARY_ADDRESS,
  UPDATE_RECENT_VEHICLES,
  UPDATE_SAVED_VEHICLE,
  UPDATE_SECONDARY_ADDRESS
} from '../../../../../../../shared/graphql/my-account-mutations';
import {
  GET_ACCOUNT_ADDRESS,
  GET_ACCOUNT_APPOINTMENTS,
  GET_ACCOUNT_ORDER,
  GET_ACCOUNT_ORDERS,
  GET_ACCOUNT_QUOTES,
  GET_ACCOUNT_RECENT_FITMENTS,
  GET_ACCOUNT_SAVED_FITMENTS,
  GET_ACCOUNT_UPDATE_EMAIL_CONFIRMATION,
  GET_ACCOUNT_USER,
  GET_ACCOUNT_VERIFICATION_BY_EMAIL
} from '../../../../../../../shared/graphql/my-account-queries';
import { GET_QUOTE_DETAILS } from '../../../../../../../shared/graphql/quote-queries';

import {
  localStorage,
  sessionStorage
} from '../../../../../../../shared/utils/storage/storage';
import {
  getMyAccountVehicleInput,
  isCustomerServiceDownErrorPresent,
  mapAccountQueryVehicle
} from '../utils/my-account-provider-utils';

import { CUSTOMER_SERVICE_IS_DOWN } from '../../../../../../../shared/constants/errors';
import {
  CONTACT_TYPES,
  HYBRIS_ACCOUNT_STATUS
} from '../../../../../../../shared/constants/my-account';
import {
  RESERVE_ONLINE_DATA,
  SERVICE_APPOINTMENT_DATA,
  SERVICE_CUSTOMER_DETAILS,
  SHOULD_REFRESH_USER_ON_APP_LOAD
} from '../../../../../../../shared/constants/storage-keys';
import {
  INITIAL_REDUCER_STATE,
  UPDATE_MY_ACCOUNT
} from '../constants/my-account-provider-constants';

async function activateAccount(token, client) {
  try {
    return await client.mutate({
      errorPolicy: 'none',
      fetchPolicy: 'no-cache',
      mutation: ACTIVATE_ACCOUNT,
      variables: {
        token
      }
    });
  } catch (err) {
    console.error('Error activating account:', err);
  }
}

function clearUserData(dispatch) {
  dispatch({ payload: INITIAL_REDUCER_STATE, type: UPDATE_MY_ACCOUNT });
}

function clearFitments(dispatch) {
  dispatch({
    payload: {
      recentAccountFitments: null,
      savedAccountFitments: null
    },
    type: UPDATE_MY_ACCOUNT
  });
}

async function deleteSecondaryAddress(client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: DELETE_ADDRESS,
      variables: {
        contactType: CONTACT_TYPES.SECONDARY
      }
    });

    if (resp?.data?.account?.deleteAddress) {
      dispatch({
        payload: {
          secondaryAddress: null
        },
        type: UPDATE_MY_ACCOUNT
      });
    } else {
      throw new Error('Secondary address not deleted');
    }
  } catch (err) {
    console.error('Unable to delete secondary address', err);
  }
}

async function getAppointments(storeCode, client, dispatch) {
  dispatch({
    payload: {
      isAppointmentsLoading: true
    },
    type: UPDATE_MY_ACCOUNT
  });

  try {
    const resp = await client.query({
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_APPOINTMENTS,
      variables: { storeCode }
    });

    dispatch({
      payload: {
        appointments: resp.data?.account?.customerAppointments,
        isAppointmentsLoading: false
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (err) {
    console.error('Failed to get appointments:', err);
    dispatch({
      payload: {
        appointmentsError: true,
        isAppointmentsLoading: false
      },
      type: UPDATE_MY_ACCOUNT
    });
  }
}

async function getOrder(token, storeCode, client, dispatch) {
  dispatch({
    payload: {
      isOrdersLoading: true
    },
    type: UPDATE_MY_ACCOUNT
  });

  try {
    let resp, order;
    resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_ORDER,
      variables: { storeCode, token }
    });

    if (resp?.errors) {
      resp = await client.query({
        errorPolicy: 'all',
        query: GET_QUOTE_DETAILS,
        variables: { quoteToken: token, storeCode }
      });
      order = resp?.data?.order?.quoteByToken;
    } else {
      order = resp?.data?.order?.orderByToken;
    }

    dispatch({
      payload: {
        isOrdersLoading: false,
        order
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (err) {
    console.error('Failed to get order:', err);
    dispatch({
      payload: {
        isOrdersLoading: false
      },
      type: UPDATE_MY_ACCOUNT
    });
  }
}

async function getOrders(storeCode, client, dispatch) {
  dispatch({
    payload: {
      isOrdersLoading: true
    },
    type: UPDATE_MY_ACCOUNT
  });

  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_ORDERS,
      variables: { storeCode }
    });

    dispatch({
      payload: {
        isOrdersLoading: false,
        orders: resp?.data?.account?.customerOrders
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (err) {
    console.error('Failed to get orders:', err);
    dispatch({
      payload: {
        isOrdersLoading: false,
        ordersError: true
      },
      type: UPDATE_MY_ACCOUNT
    });
  }
}

async function getPrimaryAddress(client, dispatch) {
  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_ADDRESS,
      variables: {
        contactType: CONTACT_TYPES.PRIMARY
      }
    });

    const primaryAddress = resp?.data?.account?.address;

    dispatch({
      payload: {
        primaryAddress
      },
      type: UPDATE_MY_ACCOUNT
    });

    return primaryAddress;
  } catch (err) {
    console.error('Failed to get primary address:', err);
  }
}

async function getSecondaryAddress(client, dispatch) {
  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_ADDRESS,
      variables: {
        contactType: CONTACT_TYPES.SECONDARY
      }
    });

    const secondaryAddress = resp?.data?.account?.address;

    dispatch({
      payload: {
        secondaryAddress
      },
      type: UPDATE_MY_ACCOUNT
    });

    return secondaryAddress;
  } catch (err) {
    console.error('Failed to get secondaryAddress address:', err);
  }
}

async function loadFitments(client, dispatch) {
  try {
    const promises = [
      client.query({
        fetchPolicy: 'network-only',
        query: GET_ACCOUNT_RECENT_FITMENTS
      }),
      client.query({
        fetchPolicy: 'network-only',
        query: GET_ACCOUNT_SAVED_FITMENTS
      })
    ];
    const [recentFitmentsResult, savedFitmentsResult] = await Promise.all(
      promises
    );
    const sortedSavedFitmentsResult =
      savedFitmentsResult?.data?.account?.savedVehicles?.sort((a, b) =>
        a.savedDate < b.savedDate ? 1 : -1
      );
    const recentAccountFitments =
      recentFitmentsResult?.data?.account?.searchVehicles?.map(vehicle =>
        mapAccountQueryVehicle(vehicle)
      );
    const savedAccountFitments = sortedSavedFitmentsResult?.map(vehicle =>
      mapAccountQueryVehicle(vehicle)
    );

    dispatch({
      payload: {
        isAccountFitmentLoaded: true,
        isCustomerServiceDown:
          isCustomerServiceDownErrorPresent(savedFitmentsResult),
        recentAccountFitments,
        savedAccountFitments
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (error) {
    console.error('error loading fitments: ', error);
  }
}

function updateSavedFitmentIsActive(fitment, isActive, client) {
  return client.mutate({
    mutation: SET_ACTIVE_STATUS_SAVED_VEHICLE,
    variables: { isActive: isActive, savedVehicleId: fitment.uid }
  });
}

function deleteSavedFitment(fitment, client) {
  return client.mutate({
    mutation: DELETE_SAVED_VEHICLE,
    variables: {
      savedVehicleId: fitment.uid
    }
  });
}

async function reconcileSavedAccountFitments(
  newSavedAccountFitments,
  client,
  dispatch,
  state
) {
  if (!Array.isArray(newSavedAccountFitments)) {
    return Promise.resolve();
  }

  const { savedAccountFitments } = state;

  const reconciliationPromises = savedAccountFitments.reduce(
    (acc, savedAccountFitment) => {
      const matchingFitment = newSavedAccountFitments.find(
        newSavedAccountFitment =>
          newSavedAccountFitment.uid === savedAccountFitment.uid
      );

      if (!matchingFitment) {
        acc.push(deleteSavedFitment(savedAccountFitment, client));
      } else if (savedAccountFitment.isActive && !matchingFitment.isActive) {
        acc.push(
          updateSavedFitmentIsActive(savedAccountFitment, false, client)
        );
      } else if (!savedAccountFitment.isActive && matchingFitment.isActive) {
        acc.push(updateSavedFitmentIsActive(savedAccountFitment, true, client));
      }

      return acc;
    },
    []
  );

  const promises = await Promise.all(reconciliationPromises);

  let isCustomerServiceDown = false;
  promises.forEach(resp => {
    if (isCustomerServiceDownErrorPresent(resp)) {
      isCustomerServiceDown = true;
    }
  });

  if (isCustomerServiceDown) {
    return CUSTOMER_SERVICE_IS_DOWN;
  } else {
    dispatch?.({
      payload: {
        savedAccountFitments: newSavedAccountFitments
      },
      type: UPDATE_MY_ACCOUNT
    });
  }
}

async function resetPassword(input, storeCode, client) {
  try {
    return await client.mutate({
      mutation: RESET_PASSWORD,
      variables: {
        storeCode,
        ...input
      }
    });
  } catch (err) {
    console.error('Failed to reset password:', err);
    return false;
  }
}

async function saveRecentVehicles(newRecentAccountFitments, client, dispatch) {
  if (!Array.isArray(newRecentAccountFitments)) {
    return Promise.resolve();
  }

  try {
    await client.mutate({
      mutation: UPDATE_RECENT_VEHICLES,
      variables: {
        vehicles: newRecentAccountFitments.map(fitment => ({
          ...getMyAccountVehicleInput(fitment),
          code: undefined
        }))
      }
    });

    dispatch?.({
      payload: {
        recentAccountFitments: newRecentAccountFitments
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (err) {
    console.error('Failed to save vehicle:', err);
  }
}

async function sendAuthenticationEmail(email, storeCode, client) {
  try {
    const resp = client.mutate({
      mutation: SEND_AUTHENTICATION_EMAIL,
      variables: {
        emailId: email,
        storeCode
      }
    });

    return resp;
  } catch (err) {
    console.error('Failure to send authentication email:', err);
    return false;
  }
}

async function reconcileAccountFitments(
  {
    recentAccountFitments: newRecentAccountFitments,
    savedAccountFitments: newSavedAccountFitments
  },
  client,
  dispatch,
  state
) {
  const isLoggedIn = Boolean(state.user && !state.userError);

  if (!isLoggedIn) {
    return;
  }

  try {
    const promises = await Promise.all([
      saveRecentVehicles(newRecentAccountFitments, client, null),
      reconcileSavedAccountFitments(
        newSavedAccountFitments,
        client,
        null,
        state
      )
    ]);

    if (promises[1] === CUSTOMER_SERVICE_IS_DOWN) {
      dispatch({
        payload: {
          isCustomerServiceDown: true
        },
        type: UPDATE_MY_ACCOUNT
      });
    } else {
      const payload = {};
      payload.recentAccountFitments =
        newRecentAccountFitments || state.recentAccountFitments;
      if (Array.isArray(newSavedAccountFitments)) {
        payload.savedAccountFitments = newSavedAccountFitments;
      }

      dispatch?.({
        payload,
        type: UPDATE_MY_ACCOUNT
      });
    }
  } catch (error) {
    console.error('error reconciling fitments: ', error);
  }
}

async function refreshUser(client, dispatch) {
  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_USER
    });

    const user = resp?.data?.account?.customer;

    if (user) {
      dispatch({
        payload: {
          user
        },
        type: UPDATE_MY_ACCOUNT
      });
      localStorage(SHOULD_REFRESH_USER_ON_APP_LOAD, true);
    }

    await loadFitments(client, dispatch);

    return resp;
  } catch (err) {
    console.error('Error refreshing user:', err);
  }
}

async function registerGuestCheckoutUser(input, client) {
  try {
    const resp = await client.mutate({
      mutation: REGISTER_GUEST_CHECKOUT_USER,
      variables: {
        registerGuestUserInput: {
          password: input?.password,
          userName: input?.username
        }
      }
    });

    return resp?.data?.account?.registerGuestCheckoutUser;
  } catch (err) {
    console.error('Failed to register guest checkout user:', err);
  }
}

async function signIn(input, options, storeCode, client, dispatch) {
  let payload;
  try {
    const resp = await client.mutate({
      mutation: SIGN_IN,
      variables: { input, storeCode }
    });
    const signInResp = resp?.data?.account?.signIn;

    if (signInResp) {
      payload = {
        user: signInResp.customerData
      };

      signInResp.customerData && (await client.resetStore());

      if (signInResp.customerData) {
        localStorage(SHOULD_REFRESH_USER_ON_APP_LOAD, true);
        sessionStorage(SERVICE_CUSTOMER_DETAILS, undefined);
      } else {
        localStorage(SHOULD_REFRESH_USER_ON_APP_LOAD, void 0);
      }

      if (typeof options?.onBeforeLoadingFitments === 'function') {
        await options.onBeforeLoadingFitments();
      }

      await loadFitments(client, dispatch);

      dispatch({ payload, type: UPDATE_MY_ACCOUNT });

      return resp;
    }
  } catch (signInError) {
    dispatch({
      payload: {
        signInError
      },
      type: UPDATE_MY_ACCOUNT
    });
    localStorage(SHOULD_REFRESH_USER_ON_APP_LOAD, void 0);
    console.error('Failed to sign in:', signInError);
  }
}

async function signOut(client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: SIGN_OUT
    });

    client.resetStore();
    clearUserData(dispatch);
    sessionStorage(RESERVE_ONLINE_DATA, undefined);
    sessionStorage(SERVICE_APPOINTMENT_DATA, undefined);
    localStorage(SHOULD_REFRESH_USER_ON_APP_LOAD, undefined);

    return resp;
  } catch (signOutError) {
    dispatch({
      payload: {
        signOutError
      },
      type: UPDATE_MY_ACCOUNT
    });
    console.error('Failure to sign out: ', signOutError);
  }
}

async function signUp(input, storeCode, client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: SIGN_UP,
      variables: { input, storeCode }
    });

    return resp;
  } catch (signUpError) {
    dispatch({
      payload: {
        signUpError
      },
      type: UPDATE_MY_ACCOUNT
    });
    console.error('Failure to sign up: ', signUpError);
  }
}

async function updateEmail(token, client) {
  try {
    const resp = await client.mutate({
      mutation: UPDATE_EMAIL,
      variables: { token }
    });

    return resp;
  } catch (err) {
    console.error('Failure to update email:', err);
    return false;
  }
}

async function updatePassword({ currentPassword, newPassword }, client) {
  try {
    return await client.mutate({
      mutation: UPDATE_PASSWORD,
      variables: {
        currentPassword,
        newPassword
      }
    });
  } catch (err) {
    console.error('Failed to update password:', err);
  }
}

async function updatePrimaryAddress(addressInput, client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: UPDATE_PRIMARY_ADDRESS,
      variables: {
        primaryAddressInput: addressInput
      }
    });

    if (resp?.data?.account?.updatePrimaryAddress) {
      getPrimaryAddress(client, dispatch);
      refreshUser(client, dispatch);
      return true;
    } else {
      throw new Error('update primary address unsuccessful');
    }
  } catch (err) {
    console.error('Failed to update primary address:', err);
    return false;
  }
}

async function updatePrimaryEmailAddress(email, client) {
  try {
    const resp = await client.query({
      query: GET_ACCOUNT_UPDATE_EMAIL_CONFIRMATION,
      variables: {
        email
      }
    });

    const isUpdated = resp?.data?.accountEmail?.updateEmail;

    if (
      [
        HYBRIS_ACCOUNT_STATUS.SUCCESSFUL,
        HYBRIS_ACCOUNT_STATUS.ACCOUNT_ALREADY_EXIST
      ].includes(isUpdated)
    ) {
      return isUpdated;
    }

    throw new Error('Email update failed');
  } catch (err) {
    console.error('Unable to update primary email address:', err);
    return false;
  }
}

async function updateSavedVehicle(fitment, client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: UPDATE_SAVED_VEHICLE,
      variables: {
        vehicle: getMyAccountVehicleInput(fitment)
      }
    });

    if (isCustomerServiceDownErrorPresent(resp)) {
      dispatch({
        payload: {
          isCustomerServiceDown: true
        },
        type: UPDATE_MY_ACCOUNT
      });
      return CUSTOMER_SERVICE_IS_DOWN;
    } else {
      return mapAccountQueryVehicle(resp?.data?.account?.updateSavedVehicle);
    }
  } catch (err) {
    console.error('Error updating saved vehicle:', err);
  }
}

async function updateSecondaryAddress(addressInput, client, dispatch) {
  try {
    const resp = await client.mutate({
      mutation: UPDATE_SECONDARY_ADDRESS,
      variables: {
        secondaryAddressInput: addressInput
      }
    });

    if (resp?.data?.account?.updateSecondaryAddress) {
      getSecondaryAddress(client, dispatch);
      refreshUser(client, dispatch);
      return true;
    } else {
      throw new Error('update secondary address unsuccessful');
    }
  } catch (err) {
    console.error('Failed to update secondary address:', err);
    return false;
  }
}

async function getQuotes(client, dispatch) {
  dispatch({
    payload: {
      isQuotesLoading: true
    },
    type: UPDATE_MY_ACCOUNT
  });

  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_QUOTES
    });

    dispatch({
      payload: {
        hasQuotesError: resp?.errors?.length > 0,
        isQuotesLoading: false,
        quotes: resp?.data?.account?.customerQuotes
      },
      type: UPDATE_MY_ACCOUNT
    });
  } catch (err) {
    console.error('Failed to get quotes:', err);
    dispatch({
      payload: {
        hasQuotesError: true,
        isQuotesLoading: false
      },
      type: UPDATE_MY_ACCOUNT
    });
  }
}

async function verifyCustomerByEmail(email, client) {
  try {
    const resp = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ACCOUNT_VERIFICATION_BY_EMAIL,
      variables: {
        email
      }
    });

    const isVerified = resp?.data?.account?.findCustomerByEmail;

    return isVerified;
  } catch (err) {
    console.error('Failed to verify customer account:', err);
  }
}

export default function myAccountProviderImpl() {
  return {
    activateAccount,
    clearFitments,
    clearUserData,
    deleteSecondaryAddress,
    getAppointments,
    getOrder,
    getOrders,
    getPrimaryAddress,
    getQuotes,
    getSecondaryAddress,
    loadFitments,
    reconcileAccountFitments,
    refreshUser,
    registerGuestCheckoutUser,
    resetPassword,
    saveRecentVehicles,
    sendAuthenticationEmail,
    signIn,
    signOut,
    signUp,
    updateEmail,
    updatePassword,
    updatePrimaryAddress,
    updatePrimaryEmailAddress,
    updateSavedVehicle,
    updateSecondaryAddress,
    verifyCustomerByEmail
  };
}
